(() => {
  'use strict';
  if (window.AWGarageContextPortability?.version) return;

  const VERSION = '1.0.0';
  const KEYS = {
    replicas: 'aw_garage_replicas_v1',
    active: 'aw_garage_active_replica_v1'
  };
  const parse = (key, fallback) => {
    try { return JSON.parse(localStorage.getItem(key) || 'null') ?? fallback; }
    catch (_) { return fallback; }
  };
  const write = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); return true; }
    catch (_) { return false; }
  };
  const replicas = () => {
    const value = parse(KEYS.replicas, []);
    return Array.isArray(value) ? value : [];
  };
  const activeSnapshot = () => {
    const id = String(parse(KEYS.active, '') || '').trim();
    const replica = id ? replicas().find((item) => String(item?.id || '') === id) : null;
    const valid = Boolean(replica && String(replica.platform || '').trim());
    return {
      version: 1,
      activeReplicaId: valid ? id : '',
      source: valid ? 'garage-explicit' : 'none'
    };
  };
  const extract = (payload = {}) => {
    const source = payload?.cloudState && typeof payload.cloudState === 'object' ? payload.cloudState : payload;
    const context = source?.personalContext && typeof source.personalContext === 'object'
      ? source.personalContext
      : (payload?.personalContext && typeof payload.personalContext === 'object' ? payload.personalContext : null);
    if (!context) return null;
    const id = String(context.activeReplicaId || '').trim();
    return id ? { activeReplicaId: id, source: String(context.source || 'garage-explicit') } : null;
  };
  const track = (name, payload = {}) => {
    try { window.AWAnalytics?.track?.(name, payload); }
    catch (_) {}
  };
  const dispatch = (replica) => {
    try { window.dispatchEvent(new StorageEvent('storage', { key: KEYS.active })); } catch (_) {}
    try {
      window.dispatchEvent(new CustomEvent('aw:garage-replica-selected', {
        detail: {
          replicaId: String(replica?.id || ''),
          replicaLabel: String(replica?.nickname || replica?.model || 'Réplique'),
          platform: String(replica?.platform || ''),
          manufacturer: String(replica?.manufacturer || ''),
          replicaType: String(replica?.type || '')
        }
      }));
    } catch (_) {}
  };
  const restore = (context, reason = 'import') => {
    const id = String(context?.activeReplicaId || '').trim();
    if (!id) return false;
    const replica = replicas().find((item) => String(item?.id || '') === id && String(item?.platform || '').trim());
    if (!replica) return false;
    if (window.AWOwnedReplicaContext?.setActiveReplica?.(id, { emit: true })) {
      track('p2_garage_personal_context_restored', { reason, replica_id: id, via: 'owned-context-api' });
      return true;
    }
    if (!write(KEYS.active, id)) return false;
    dispatch(replica);
    track('p2_garage_personal_context_restored', { reason, replica_id: id, via: 'local-context' });
    return true;
  };

  const state = () => window.AWGarageP2?.getState?.() || {
    replicas: parse('aw_garage_replicas_v1', []),
    timeline: parse('aw_upgrade_timeline_v1', []),
    buildLinks: parse('aw_build_replica_links_v1', {}),
    builds: parse('aw_airsoft_saved_configurations', [])
  };
  const standardEnvelope = () => ({
    version: 3,
    exportedAt: new Date().toISOString(),
    ...state(),
    personalContext: activeSnapshot()
  });
  const cloudEnvelope = () => {
    const base = window.AWP2CloudExportBridge?.envelope?.();
    if (!base || typeof base !== 'object') return null;
    const payload = base;
    if (!payload.cloudState || typeof payload.cloudState !== 'object') payload.cloudState = {};
    payload.cloudState.personalContext = activeSnapshot();
    payload.cloudState.schemaVersion = Math.max(3, Number(payload.cloudState.schemaVersion || 0) || 0);
    payload.schemaVersion = Math.max(3, Number(payload.schemaVersion || 0) || 0);
    return payload;
  };
  const download = (payload) => {
    try {
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `aw-garage-${new Date().toISOString().slice(0,10)}.json`;
      link.click();
      URL.revokeObjectURL(link.href);
      return true;
    } catch (_) { return false; }
  };
  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text); return true; }
    catch (_) {
      try {
        const area = document.createElement('textarea');
        area.value = text;
        area.setAttribute('readonly', '');
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        const ok = document.execCommand('copy');
        area.remove();
        return ok;
      } catch (_) { return false; }
    }
  };

  let pendingImport = null;
  let pendingReason = 'manual_import';
  const scheduleRestore = () => {
    if (!pendingImport) return;
    [0, 80, 250, 700].forEach((delay) => window.setTimeout(() => {
      if (!pendingImport) return;
      if (restore(pendingImport, pendingReason)) pendingImport = null;
    }, delay));
  };

  document.addEventListener('click', async (event) => {
    const standard = event.target?.closest?.('[data-aw-garage-export]');
    if (standard) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const payload = standardEnvelope();
      const ok = download(payload);
      track('p2_exported', { version: 3, personal_context_active: Boolean(payload.personalContext?.activeReplicaId), ok });
      return;
    }

    const cloud = event.target?.closest?.('[data-aw-p2-cloud-copy]');
    if (!cloud) return;
    const payload = cloudEnvelope();
    if (!payload) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const text = JSON.stringify(payload);
    const initial = cloud.textContent;
    const ok = await copyText(text);
    cloud.textContent = ok ? 'Snapshot Cloud copié ✓' : 'Copie impossible';
    window.setTimeout(() => { cloud.textContent = initial; }, 2200);
    track('p2_cloud_migration_copied', {
      bytes: text.length,
      schema_version: 3,
      personal_context_active: Boolean(payload.cloudState?.personalContext?.activeReplicaId),
      ok
    });
  }, true);

  document.addEventListener('change', (event) => {
    const input = event.target?.closest?.('[data-aw-garage-import]');
    const file = input?.files?.[0];
    if (!file) return;
    file.text().then((text) => {
      let payload = null;
      try { payload = JSON.parse(text); } catch (_) { return; }
      const context = extract(payload);
      if (!context) return;
      pendingImport = context;
      pendingReason = 'manual_import';
      scheduleRestore();
    }).catch(() => {});
  }, true);

  window.addEventListener('aw:p2-garage-updated', scheduleRestore);
  window.addEventListener('aw:p2-cloud-data', (event) => {
    const context = extract(event.detail || {});
    if (!context) return;
    pendingImport = context;
    pendingReason = 'cloud_import';
    scheduleRestore();
  });

  window.AWGarageContextPortability = {
    version: VERSION,
    activeSnapshot,
    standardEnvelope,
    cloudEnvelope,
    extract,
    restore,
    policy: 'explicit-active-id-only;validate-imported-replica-and-platform;never-auto-select-first;no-compatibility-proof'
  };
})();
